import { pantryColors, pantryTypography } from '@pantry/ui'
import React from 'react'

const p = pantryColors
const F = pantryTypography.fontFamily

interface WalletTextFieldProps {
  label: string
  value: string
  onChange: (next: string) => void
  placeholder?: string
  helperText?: string
  error?: string
  multiline?: boolean
  rows?: number
  type?: 'text' | 'url'
}

// Stacked label + input pattern used inside the Wallet cards. The Rules tab
// uses a horizontal RuleRow because all controls are small numeric inputs;
// URL / long-text inputs need full row width for readability so we stack.
export const WalletTextField: React.FC<WalletTextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  helperText,
  error,
  multiline = false,
  rows = 3,
  type = 'text',
}) => {
  const borderColor = error ? '#DC2626' : p.gray[200]
  const sharedStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${borderColor}`,
    borderRadius: 6,
    fontSize: 13,
    color: p.gray[700],
    background: p.white,
    outline: 'none',
    fontFamily: F,
    boxSizing: 'border-box',
  }
  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!error) {
      e.currentTarget.style.borderColor = p.brand.primary
      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,.1)'
    }
  }
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    e.currentTarget.style.borderColor = borderColor
    e.currentTarget.style.boxShadow = 'none'
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '10px 0',
        fontFamily: F,
      }}
    >
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: p.gray[700],
        }}
      >
        {label}
      </div>
      {multiline ? (
        <textarea
          value={value}
          rows={rows}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{ ...sharedStyle, resize: 'vertical', lineHeight: 1.5 }}
        />
      ) : (
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={sharedStyle}
        />
      )}
      {error ? (
        <div style={{ fontSize: 11.5, color: '#DC2626', fontWeight: 500 }}>
          {error}
        </div>
      ) : helperText ? (
        <div style={{ fontSize: 11.5, color: p.gray[400], lineHeight: 1.4 }}>
          {helperText}
        </div>
      ) : null}
    </div>
  )
}
